import { useState } from 'react';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { formatDate, formatDateTime } from '../formatos';
import { BotaoNu } from './comum';
import { ROTULO_DA_ORIGEM, palavrasDoHtml, type BlocoComparado, type VersaoDoTexto } from '../versoes';

/**
 * Painel "Versões" (lateral) e a folha de comparação.
 *
 * A lista vem do app (ou da memória, se o app não guardar); clicar numa
 * versão mostra na folha o que mudou dela até o texto atual. Voltar a uma
 * versão guarda antes o texto de agora — nada se perde.
 */

const hora = (iso: string) => formatDateTime(iso).split(' às ')[1] ?? '';

function dia(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - 1);
  const mesmoDia = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (mesmoDia(d, hoje)) return 'Hoje';
  if (mesmoDia(d, ontem)) return 'Ontem';
  return formatDate(iso);
}

export function quandoDaVersao(v: VersaoDoTexto): string {
  return `${dia(v.criadaEm) === 'Hoje' ? 'hoje' : dia(v.criadaEm) === 'Ontem' ? 'ontem' : formatDate(v.criadaEm)} às ${hora(v.criadaEm)}`;
}

export function ConteudoVersoes({ versoes, selecionada, podeExcluir, somenteLeitura, aoSelecionar, aoNomear, aoExcluir }: {
  versoes: VersaoDoTexto[];
  selecionada: string | null;
  podeExcluir: boolean;
  somenteLeitura: boolean;
  aoSelecionar: (id: string | null) => void;
  aoNomear: (rotulo: string) => void;
  aoExcluir: (id: string) => void;
}) {
  const [nomeando, setNomeando] = useState(false);
  const [nome, setNome] = useState('');
  const salvarNome = () => {
    if (!nome.trim()) return;
    aoNomear(nome.trim());
    setNome('');
    setNomeando(false);
  };

  // Mais nova primeiro, agrupada por dia.
  const ordem = [...versoes].sort((a, b) => b.criadaEm.localeCompare(a.criadaEm));
  const dias: { dia: string; lista: VersaoDoTexto[] }[] = [];
  for (const v of ordem) {
    const d = dia(v.criadaEm);
    const ult = dias[dias.length - 1];
    if (ult && ult.dia === d) ult.lista.push(v); else dias.push({ dia: d, lista: [v] });
  }

  return (
    <>
      {!somenteLeitura && (nomeando ? (
        <div className="cm-ef-versao-nome">
          <label className="cm-ef-rot">Nome da versão
            <input className="cm-ef-campo" value={nome} autoFocus placeholder="Ex.: Antes da réplica" maxLength={80}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); salvarNome(); } else if (e.key === 'Escape') setNomeando(false); }} />
          </label>
          <div className="cm-ef-linha-bt">
            <Button type="button" size="sm" variant="outline" onClick={() => setNomeando(false)}>Cancelar</Button>
            <Button type="button" size="sm" disabled={!nome.trim()} onClick={salvarNome}>Guardar versão</Button>
          </div>
        </div>
      ) : (
        <Button type="button" size="sm" variant="outline" className="cm-ef-versao-nova" onClick={() => setNomeando(true)}>Guardar esta versão com um nome…</Button>
      ))}

      {!versoes.length && (
        <p className="cm-ef-mini-leg">
          Ainda não há versões. Cada vez que você salva, o editor guarda uma; o salvamento automático, uma a cada 10 minutos.
          Antes de a IA reescrever o texto ou de você voltar a uma versão, o texto de agora também fica guardado.
        </p>
      )}

      {dias.map((g) => (
        <section key={g.dia} className="cm-ef-versao-dia" aria-label={g.dia}>
          <h3>{g.dia}</h3>
          <ul>
            {g.lista.map((v) => {
              const ativa = v.id === selecionada;
              return (
                <li key={v.id} className={`cm-ef-versao${ativa ? ' is-ativa' : ''}`}>
                  <BotaoNu className="cm-ef-versao-bt" aria-pressed={ativa} onClick={() => aoSelecionar(ativa ? null : v.id)}>
                    <span className="cm-ef-versao-hora">{hora(v.criadaEm)}</span>
                    <span className="cm-ef-versao-txt">
                      <b>{v.rotulo || ROTULO_DA_ORIGEM[v.origem]}</b>
                      <span>
                        {v.rotulo && v.origem !== 'nomeada' ? `${ROTULO_DA_ORIGEM[v.origem]} · ` : ''}
                        {(v.palavras ?? palavrasDoHtml(v.html ?? '')).toLocaleString('pt-BR')} palavras{v.autor ? ` · ${v.autor}` : ''}
                      </span>
                    </span>
                  </BotaoNu>
                  {podeExcluir && (
                    <BotaoNu className="cm-ef-bt cm-ef-versao-excluir" aria-label={`Excluir a versão das ${hora(v.criadaEm)}`} onClick={() => aoExcluir(v.id)}>
                      <svg viewBox="0 0 24 24" className="cm-ef-ico" aria-hidden><path d="M5 7h14M10 11v6M14 11v6M6 7l1 12h10l1-12M9 7V4h6v3" /></svg>
                    </BotaoNu>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </>
  );
}

/** A folha em modo comparação: o texto de agora, com o que mudou desde a versão marcado. */
export function FolhaComparacao({ blocos, versao, contra, somenteLeitura, aoVoltar, aoFechar, aoTrocarContra, temAnterior }: {
  blocos: BlocoComparado[];
  versao: VersaoDoTexto;
  contra: 'atual' | 'anterior';
  temAnterior: boolean;
  somenteLeitura: boolean;
  aoVoltar: () => void;
  aoFechar: () => void;
  aoTrocarContra: (c: 'atual' | 'anterior') => void;
}) {
  const mudancas = blocos.filter((b) => b.tipo === 'mudou').length;
  return (
    <>
      <div className="cm-ef-comparando" role="status">
        <div className="cm-ef-comparando-txt">
          <b>{contra === 'atual' ? `Da versão de ${quandoDaVersao(versao)} até agora` : `Da versão anterior até a de ${quandoDaVersao(versao)}`}</b>
          <span>
            {mudancas ? `${mudancas} ${mudancas === 1 ? 'trecho mudou' : 'trechos mudaram'}. ` : 'Nenhuma diferença no texto. '}
            <ins>Sublinhado</ins> entrou depois; <del>riscado</del> saiu.
          </span>
        </div>
        <div className="cm-ef-linha-bt">
          {temAnterior && (
            <span className="cm-ef-seg-mini" role="group" aria-label="Comparar">
              <BotaoNu aria-pressed={contra === 'atual'} onClick={() => aoTrocarContra('atual')}>Com o texto atual</BotaoNu>
              <BotaoNu aria-pressed={contra === 'anterior'} onClick={() => aoTrocarContra('anterior')}>Com a anterior</BotaoNu>
            </span>
          )}
          <Button type="button" size="sm" variant="outline" onClick={aoFechar}>Fechar comparação</Button>
          {!somenteLeitura && <Button type="button" size="sm" onClick={aoVoltar}>Voltar a esta versão</Button>}
        </div>
      </div>
      <div className="cm-ef-texto cm-ef-comparacao" aria-label="Comparação de versões">
        {blocos.map((b, i) => (b.tipo === 'igual'
          ? <div key={i} className="cm-ef-cmp-igual" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.html) }} />
          : <div key={i} className={`cm-ef-cmp-mudou is-${b.trecho}`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.html) }} />))}
      </div>
    </>
  );
}
